import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button, Dropdown, Input } from 'semantic-ui-react'

import DomainDropdown from 'components/DomainDropdown'
import EmailSurveyTemplatePreview from 'components/EmailSurveyTemplatePreview'
import RichEditor from 'components/RichEditor'
import { draftEditorToHTMLCode, convertHTMLToDraftEditor } from 'components/RichEditor/utils'
import ErrorText from 'shared/components/forms/ErrorText'
import { getHTMLFromTemplateID } from './container'

const REQUIRED_FIELD_ERROR = 'This field is required.'

/**
 * Wraps the prefix `<input>` and DomainDropdown as a flush action-input pair,
 * locking each half to 50% so a long domain doesn't squeeze the prefix input.
 * Layout-only — no border/shape overrides; the seam comes from SUI's stock
 * `.ui.action.input` styling.
 */
const DomainActionInput = styled(Input)`
  &.ui.action.input {
    width: 100%;
  }
  &.ui.action.input > input,
  &.ui.action.input > .ui.dropdown {
    flex-basis: 50%;
    min-width: 0;
  }
`

const VARIABLES = [
  {
    name: 'First name',
    value: 'first_name',
  },
  {
    name: 'Company',
    value: 'company',
  },
]

const EVENT_BASED_VARIABLES = [
  {
    name: 'Customer first name',
    value: 'customer_first_name',
  },
  {
    name: 'Company',
    value: 'customer_company',
  },
  {
    name: 'Team member first name',
    value: 'team_member_first_name',
  },

  {
    name: 'Ticket ID',
    value: 'ticket_id',
  },
  {
    name: 'Ticket subject',
    value: 'ticket_subject',
  },
]

/**
 * @typedef DomainOption
 * @property {string} text
 * @property {string} value
 * @property {'validated' | 'failed' | 'pending'} status
 */

/**
 * @typedef Template
 * @property {number} email_template
 * @property {string=} intro
 * @property {string=} outro
 * @property {string} sender_name
 * @property {string} sender_email
 * @property {string} subject
 * @property {string=} html
 */

/**
 * @typedef TemplateOption
 * @property {number} id
 * @property {string} name
 * @property {string} html
 * @property {string} screenshot
 */

/**
 *
 * @param {object} props
 * @param {DomainOption[]} props.domains
 * @param {Template} props.defaultTemplate
 * @param {TemplateOption[]} props.templates
 * @param {string} props.surveyType
 * @param {function} props.onChange
 * @param {function} props.handleSendTestModalOpen
 */
function SurveyEmailSettings({
  domains,
  defaultTemplate,
  templates,
  surveyType,
  onChange,
  handleSendTestModalOpen,
  isDefaultLanguage,
}) {
  const showRequiredFieldErrors = isDefaultLanguage
  const isEmailDisabled = !isDefaultLanguage
  const template = defaultTemplate

  const [prefixEmail, emailDomain] = (defaultTemplate.sender_email || '').split('@')
  const [senderPrefixEmail, setSenderPrefixEmail] = useState(prefixEmail)
  const [senderEmailDomain, setSenderEmailDomain] = useState(`@${emailDomain}`)

  const intro = useMemo(
    () => convertHTMLToDraftEditor(defaultTemplate.intro || ''),
    [defaultTemplate.intro]
  )
  const outro = useMemo(
    () => convertHTMLToDraftEditor(defaultTemplate.outro || ''),
    [defaultTemplate.outro]
  )

  function _handleTemplateChange(key, value) {
    onChange({
      ...template,
      [key]: value,
    })
  }

  function handleDomainChange(e) {
    setSenderEmailDomain(e.target.innerText)
    _handleTemplateChange('sender_email', `${senderPrefixEmail}${e.target.innerText}`)
  }

  function handleSenderEmailChange(e) {
    setSenderPrefixEmail(e.target.value)
    _handleTemplateChange('sender_email', `${e.target.value}${senderEmailDomain}`)
  }

  function handleSenderNameChange(e) {
    _handleTemplateChange('sender_name', e.target.value)
  }

  function handleSubjectChange(e) {
    _handleTemplateChange('subject', e.target.value)
  }

  function handleTemplateChange(_, option) {
    const email_template = option.value
    onChange({
      ...template,
      email_template,
      html: getHTMLFromTemplateID(email_template, templates),
    })
  }

  function handleIntroChange(editorState) {
    _handleTemplateChange('intro', draftEditorToHTMLCode(editorState))
  }

  function handleOutroChange(editorState) {
    _handleTemplateChange('outro', draftEditorToHTMLCode(editorState))
  }

  return (
    <form className="ui form">
      <div className="ui stackable grid">
        <div className="five wide column ">
          <div className="field">
            <RichEditor
              fieldName="Intro text"
              onTextChange={handleIntroChange}
              defaultContent={intro}
              variables={surveyType == 'event_based_email' ? EVENT_BASED_VARIABLES : VARIABLES}
            />
          </div>
          <div className="field">
            <RichEditor
              fieldName="Outro text"
              onTextChange={handleOutroChange}
              defaultContent={outro}
              variables={surveyType == 'event_based_email' ? EVENT_BASED_VARIABLES : VARIABLES}
            />
          </div>
          <div
            className={`field${
              showRequiredFieldErrors && !(template.sender_name || '').trim() ? ' error' : ''
            }`}
          >
            <label>From</label>
            <input
              type="text"
              name="senderName"
              required
              value={template.sender_name}
              onChange={handleSenderNameChange}
            />
            {showRequiredFieldErrors && !(template.sender_name || '').trim() && (
              <ErrorText>{REQUIRED_FIELD_ERROR}</ErrorText>
            )}
          </div>
          <div
            className={`field${
              showRequiredFieldErrors && !(senderPrefixEmail || '').trim() ? ' error' : ''
            }`}
          >
            <label>Email</label>
            <DomainActionInput
              fluid
              name="senderPrefixEmail"
              required
              value={senderPrefixEmail}
              onChange={handleSenderEmailChange}
              disabled={isEmailDisabled}
              /* Opt out of password-manager autofill on this field — it is a
                 sender-domain picker, not a credentials form. Dashlane / 1Password
                 / LastPass inject a helper <span> as a sibling of the dropdown
                 when they latch onto the prefix input, which breaks SUI's stock
                 `> .dropdown:last-child` border-radius rule (sharp right corners
                 instead of rounded). These attrs ride the `input` shorthand so
                 they land on the inner <input> where the extensions hook in. */
              input={{
                'data-form-type': 'other',
                'data-1p-ignore': true,
                'data-lpignore': 'true'
              }}
              action={
                <DomainDropdown
                  domains={domains}
                  selectedEmailDomain={senderEmailDomain}
                  handleDomainChange={handleDomainChange}
                  disabled={isEmailDisabled}
                />
              }
            />
            {showRequiredFieldErrors && !(senderPrefixEmail || '').trim() && (
              <ErrorText>{REQUIRED_FIELD_ERROR}</ErrorText>
            )}
          </div>
          <div
            className={`field${
              showRequiredFieldErrors && !(template.subject || '').trim() ? ' error' : ''
            }`}
          >
            <label>Subject</label>
            <input
              type="text"
              name="subject"
              required
              value={template.subject}
              onChange={handleSubjectChange}
            />
            {showRequiredFieldErrors && !(template.subject || '').trim() && (
              <ErrorText>{REQUIRED_FIELD_ERROR}</ErrorText>
            )}
          </div>
          <div className="field">
            <label>Design template</label>
            <Dropdown
              fluid
              selection
              defaultValue={template.email_template}
              onChange={handleTemplateChange}
              options={templates.map((template) => ({
                key: template.id,
                text: template.name,
                value: template.id,
              }))}
            />
          </div>
          <div className="field">
            <Button fluid type="button" onClick={handleSendTestModalOpen}>
              <i className="far fa-paper-plane mr2" />
              Send test email
            </Button>
          </div>
        </div>
        <div className="eleven wide column ">
          <EmailSurveyTemplatePreview
            introHTML={draftEditorToHTMLCode(intro)}
            outroHTML={draftEditorToHTMLCode(outro)}
            template={template.html}
            senderName={template.sender_name}
            senderEmail={senderPrefixEmail}
            domain={senderEmailDomain}
            subject={template.subject}
          />
        </div>
      </div>
    </form>
  )
}

export default SurveyEmailSettings



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/SurveyEmailSettings/index.js